'use client'

import {
    createLetterhead,
    getLetterhead,
    Letterhead,
    LetterheadMode,
    LetterheadPageSize,
    LetterheadPayload,
    LetterheadWatermarkType,
    updateLetterhead,
} from '@/lib/api/letterheads/letterhead-api'
import {
    LETTERHEAD_DOCUMENT_GROUPS,
    LETTERHEAD_DOCUMENT_TYPES,
} from '@mediardex/contracts'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdInfoOutline, MdSave } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import LetterheadImageField from './letterhead-image-field'

/**
 * Explicação de cada modo. O texto é longo de propósito: a diferença entre
 * "desenhar a arte" e "só reservar o espaço" é o que separa quem imprime em
 * papel comum de quem já mandou imprimir o papel na gráfica, e escolher errado
 * produz um documento com o timbre duplicado ou nenhum.
 */
const MODE_OPTIONS: {
    value: LetterheadMode
    label: string
    description: string
}[] = [
    {
        value: 'BANDS',
        label: 'Cabeçalho e rodapé',
        description:
            'Duas imagens em faixa, no topo e na base da página. O sistema calcula a altura de cada uma pela proporção do arquivo e reserva o espaço sozinho.',
    },
    {
        value: 'FULL_PAGE',
        label: 'Página inteira',
        description:
            'Uma arte que ocupa a folha toda, ao fundo. Use quando o timbrado já veio pronto do designer; as margens abaixo dizem onde o texto pode começar.',
    },
    {
        value: 'RESERVE',
        label: 'Somente margens',
        description:
            'Não desenha nada — apenas evita que o texto caia em cima da arte. É a opção para quem imprime em papel timbrado já impresso na gráfica.',
    },
]

const DEFAULT_MARGINS_BY_MODE: Record<
    LetterheadMode,
    { top: number; bottom: number; left: number; right: number }
> = {
    // No modo de faixas a margem é a folga ENTRE a faixa e o texto, então um
    // valor pequeno basta. Nos outros ela é medida da borda do papel.
    BANDS: { top: 8, bottom: 8, left: 15, right: 15 },
    FULL_PAGE: { top: 35, bottom: 30, left: 20, right: 20 },
    RESERVE: { top: 35, bottom: 30, left: 20, right: 20 },
}

export default function LetterheadsFormPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(isEditing)
    const [saving, setSaving] = useState(false)
    const [letterhead, setLetterhead] = useState<Letterhead | null>(null)

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [active, setActive] = useState(true)
    const [isDefault, setIsDefault] = useState(false)
    const [mode, setMode] = useState<LetterheadMode>('BANDS')
    const [pageSize, setPageSize] = useState<LetterheadPageSize>('A4')
    const [documentTypes, setDocumentTypes] = useState<string[]>([])
    const [backgroundFirstPageOnly, setBackgroundFirstPageOnly] = useState(false)

    const [marginTopMm, setMarginTopMm] = useState(8)
    const [marginBottomMm, setMarginBottomMm] = useState(8)
    const [marginLeftMm, setMarginLeftMm] = useState(15)
    const [marginRightMm, setMarginRightMm] = useState(15)

    const [watermarkType, setWatermarkType] =
        useState<LetterheadWatermarkType>('NONE')
    const [watermarkText, setWatermarkText] = useState('')
    const [watermarkOpacity, setWatermarkOpacity] = useState(0.08)
    const [watermarkRotation, setWatermarkRotation] = useState(-45)

    const [hideDefaultHeader, setHideDefaultHeader] = useState(true)
    const [hideDefaultFooter, setHideDefaultFooter] = useState(true)

    useEffect(() => {
        if (isEditing) loadLetterhead()
    }, [id])

    const applyLetterhead = (data: Letterhead) => {
        setLetterhead(data)
        setName(data.name)
        setDescription(data.description || '')
        setActive(data.active)
        setIsDefault(data.isDefault)
        setMode(data.mode)
        setPageSize(data.pageSize)
        setDocumentTypes(data.documentTypes || [])
        setBackgroundFirstPageOnly(data.backgroundFirstPageOnly)
        setMarginTopMm(data.marginTopMm)
        setMarginBottomMm(data.marginBottomMm)
        setMarginLeftMm(data.marginLeftMm)
        setMarginRightMm(data.marginRightMm)
        setWatermarkType(data.watermarkType)
        setWatermarkText(data.watermarkText || '')
        setWatermarkOpacity(data.watermarkOpacity)
        setWatermarkRotation(data.watermarkRotation)
        setHideDefaultHeader(data.hideDefaultHeader)
        setHideDefaultFooter(data.hideDefaultFooter)
    }

    const loadLetterhead = async () => {
        setLoading(true)
        try {
            const response = await getLetterhead(Number(id))
            if (response.success && response.result) {
                applyLetterhead(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar o timbrado')
                navigate('/registrations/letterheads')
            }
        } finally {
            setLoading(false)
        }
    }

    const handleModeChange = (nextMode: LetterheadMode) => {
        setMode(nextMode)
        // Só sugere as margens do modo em um cadastro novo: num timbrado já
        // ajustado, sobrescrever os números medidos na régua seria uma perda.
        if (!isEditing) {
            const defaults = DEFAULT_MARGINS_BY_MODE[nextMode]
            setMarginTopMm(defaults.top)
            setMarginBottomMm(defaults.bottom)
            setMarginLeftMm(defaults.left)
            setMarginRightMm(defaults.right)
        }
    }

    const toggleDocumentType = (type: string) => {
        setDocumentTypes(current =>
            current.includes(type)
                ? current.filter(item => item !== type)
                : [...current, type],
        )
    }

    const buildPayload = (): LetterheadPayload => ({
        name: name.trim(),
        description: description.trim() || undefined,
        active,
        isDefault,
        mode,
        pageSize,
        documentTypes,
        backgroundFirstPageOnly,
        marginTopMm,
        marginBottomMm,
        marginLeftMm,
        marginRightMm,
        watermarkType,
        watermarkText: watermarkText.trim() || undefined,
        watermarkOpacity,
        watermarkRotation,
        hideDefaultHeader,
        hideDefaultFooter,
    })

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        if (!name.trim()) {
            toast.error('Informe um nome para o timbrado')
            return
        }

        setSaving(true)
        try {
            if (isEditing) {
                const response = await updateLetterhead(
                    Number(id),
                    buildPayload(),
                )
                if (response.success) {
                    toast.success('Timbrado atualizado')
                    navigate('/registrations/letterheads')
                } else {
                    toast.error(response.error || 'Erro ao salvar o timbrado')
                }
            } else {
                const response = await createLetterhead(buildPayload())
                if (response.success && response.result) {
                    toast.success(
                        'Timbrado criado. Envie as imagens para concluir.',
                    )
                    // Não volta para a lista: as imagens só podem ser enviadas
                    // depois que o registro existe, e é isso que falta fazer.
                    navigate(
                        `/registrations/letterheads/${response.result.id}`,
                        { replace: true },
                    )
                } else {
                    toast.error(response.error || 'Erro ao criar o timbrado')
                }
            }
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    const selectedMode = MODE_OPTIONS.find(option => option.value === mode)

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/registrations/letterheads')}
                    className="btn btn-ghost btn-sm btn-circle"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                    {isEditing ? 'Editar Timbrado' : 'Novo Timbrado'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Identificação */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Identificação
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome *
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="Ex: Papel timbrado da clínica"
                                required
                            />
                        </div>

                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Descrição
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Para que serve este timbrado"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tamanho do papel
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={pageSize}
                                onChange={e =>
                                    setPageSize(
                                        e.target.value as LetterheadPageSize,
                                    )
                                }
                            >
                                <option value="A4">A4</option>
                                <option value="A5">A5</option>
                            </select>
                            <p className="text-xs text-base-content/60 mt-1 px-1">
                                A arte é aplicada só em documentos deste tamanho
                                — esticada em outro, ela distorce.
                            </p>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Situação
                                </span>
                            </label>
                            <div className="flex flex-col gap-4 pt-3 px-1">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary toggle-sm"
                                        checked={active}
                                        onChange={e =>
                                            setActive(e.target.checked)
                                        }
                                    />
                                    <span className="text-sm">Ativo</span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary toggle-sm"
                                        checked={isDefault}
                                        onChange={e =>
                                            setIsDefault(e.target.checked)
                                        }
                                    />
                                    <span className="text-sm">
                                        Timbrado padrão da filial
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Formato */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Formato
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {MODE_OPTIONS.map(option => (
                            <label
                                key={option.value}
                                className={`cursor-pointer rounded-xl border p-4 flex flex-col gap-2 transition-all ${
                                    mode === option.value
                                        ? 'border-secondary bg-secondary/5 shadow-sm'
                                        : 'border-base-300/50 hover:border-base-300'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        name="letterhead-mode"
                                        className="radio radio-secondary radio-sm"
                                        checked={mode === option.value}
                                        onChange={() =>
                                            handleModeChange(option.value)
                                        }
                                    />
                                    <span className="font-medium text-sm">
                                        {option.label}
                                    </span>
                                </div>
                                <span className="text-xs text-base-content/60 leading-relaxed">
                                    {option.description}
                                </span>
                            </label>
                        ))}
                    </div>

                    {selectedMode && (
                        <div className="alert alert-info mt-4 py-3">
                            <MdInfoOutline className="text-xl shrink-0" />
                            <span className="text-sm">
                                {mode === 'BANDS'
                                    ? 'As margens abaixo são a folga entre a faixa e o texto.'
                                    : 'As margens abaixo são medidas a partir da borda do papel — é onde o texto pode começar.'}
                            </span>
                        </div>
                    )}
                </div>

                {/* Margens */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Margens
                    </h2>
                    <p className="text-sm text-base-content/60 mb-4">
                        Em milímetros, a mesma unidade da régua e da gráfica.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            {
                                label: 'Superior',
                                value: marginTopMm,
                                set: setMarginTopMm,
                            },
                            {
                                label: 'Inferior',
                                value: marginBottomMm,
                                set: setMarginBottomMm,
                            },
                            {
                                label: 'Esquerda',
                                value: marginLeftMm,
                                set: setMarginLeftMm,
                            },
                            {
                                label: 'Direita',
                                value: marginRightMm,
                                set: setMarginRightMm,
                            },
                        ].map(field => (
                            <div className="form-control" key={field.label}>
                                <label className="label">
                                    <span className="label-text font-medium">
                                        {field.label}
                                    </span>
                                </label>
                                <label className="input input-bordered flex items-center gap-2">
                                    <input
                                        type="number"
                                        className="grow"
                                        min={0}
                                        max={150}
                                        step={0.5}
                                        value={field.value}
                                        onChange={e =>
                                            field.set(Number(e.target.value))
                                        }
                                    />
                                    <span className="text-base-content/50 text-sm">
                                        mm
                                    </span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Imagens */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Imagens
                    </h2>
                    <p className="text-sm text-base-content/60 mb-4">
                        PNG, JPEG ou WebP, até 2 MB. Exporte em 150 dpi: a arte é
                        embarcada no PDF, e um arquivo grande pesa em todo
                        documento gerado.
                    </p>

                    {!letterhead ? (
                        <div className="alert py-3">
                            <MdInfoOutline className="text-xl shrink-0" />
                            <span className="text-sm">
                                Salve o timbrado para liberar o envio das
                                imagens.
                            </span>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-5">
                            {mode === 'BANDS' && (
                                <>
                                    <LetterheadImageField
                                        letterheadId={letterhead.id}
                                        slot="header"
                                        title="Cabeçalho"
                                        hint="Faixa do topo, de borda a borda. A altura é calculada pela proporção do arquivo."
                                        url={letterhead.headerImageUrl}
                                        width={letterhead.headerImageWidth}
                                        height={letterhead.headerImageHeight}
                                        onChange={applyLetterhead}
                                    />
                                    <LetterheadImageField
                                        letterheadId={letterhead.id}
                                        slot="footer"
                                        title="Rodapé"
                                        hint="Faixa da base, de borda a borda."
                                        url={letterhead.footerImageUrl}
                                        width={letterhead.footerImageWidth}
                                        height={letterhead.footerImageHeight}
                                        onChange={applyLetterhead}
                                    />
                                </>
                            )}

                            {mode === 'FULL_PAGE' && (
                                <>
                                    <LetterheadImageField
                                        letterheadId={letterhead.id}
                                        slot="background"
                                        title="Arte da página"
                                        hint={`Imagem no formato do papel (${pageSize}), aplicada ao fundo de cada página.`}
                                        url={letterhead.backgroundImageUrl}
                                        width={letterhead.backgroundImageWidth}
                                        height={letterhead.backgroundImageHeight}
                                        onChange={applyLetterhead}
                                    />
                                    <label className="label cursor-pointer justify-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-secondary toggle-sm"
                                            checked={backgroundFirstPageOnly}
                                            onChange={e =>
                                                setBackgroundFirstPageOnly(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                        <span className="label-text">
                                            Aplicar a arte apenas na primeira
                                            página
                                        </span>
                                    </label>
                                </>
                            )}

                            {mode === 'RESERVE' && (
                                <div className="alert py-3">
                                    <MdInfoOutline className="text-xl shrink-0" />
                                    <span className="text-sm">
                                        Neste modo o sistema não desenha imagem
                                        alguma — o timbre é o do papel que já sai
                                        impresso da gráfica.
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Marca d'água */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Marca d’água
                    </h2>
                    <p className="text-sm text-base-content/60 mb-4">
                        Aplicada por cima do conteúdo, com transparência. Serve
                        tanto para avisos — “2ª VIA”, “CÓPIA” — quanto para o
                        símbolo da empresa esmaecido, que dificulta reimprimir o
                        documento como se fosse o original.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={watermarkType}
                                onChange={e =>
                                    setWatermarkType(
                                        e.target
                                            .value as LetterheadWatermarkType,
                                    )
                                }
                            >
                                <option value="NONE">Sem marca d’água</option>
                                <option value="TEXT">Texto</option>
                                <option value="IMAGE">Imagem</option>
                            </select>
                        </div>

                        {watermarkType === 'TEXT' && (
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Texto
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    value={watermarkText}
                                    onChange={e =>
                                        setWatermarkText(e.target.value)
                                    }
                                    placeholder="Ex: 2ª VIA"
                                    maxLength={60}
                                />
                            </div>
                        )}

                        {watermarkType !== 'NONE' && (
                            <>
                                <div className="form-control">
                                    <div className="flex items-center justify-between gap-2 mb-2 px-1">
                                        <span className="label-text font-medium">
                                            Transparência
                                        </span>
                                        <span className="text-xs text-base-content/60">
                                            {Math.round(watermarkOpacity * 100)}
                                            %
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        className="range range-secondary range-sm"
                                        min={2}
                                        max={50}
                                        value={Math.round(
                                            watermarkOpacity * 100,
                                        )}
                                        onChange={e =>
                                            setWatermarkOpacity(
                                                Number(e.target.value) / 100,
                                            )
                                        }
                                    />
                                    <p className="text-xs text-base-content/60 mt-2 px-1">
                                        Acima de 15% a marca começa a competir
                                        com o texto do documento.
                                    </p>
                                </div>

                                <div className="form-control">
                                    <div className="flex items-center justify-between gap-2 mb-2 px-1">
                                        <span className="label-text font-medium">
                                            Inclinação
                                        </span>
                                        <span className="text-xs text-base-content/60">
                                            {watermarkRotation}°
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        className="range range-secondary range-sm"
                                        min={-90}
                                        max={90}
                                        step={5}
                                        value={watermarkRotation}
                                        onChange={e =>
                                            setWatermarkRotation(
                                                Number(e.target.value),
                                            )
                                        }
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {watermarkType === 'IMAGE' && letterhead && (
                        <div className="mt-4">
                            <LetterheadImageField
                                letterheadId={letterhead.id}
                                slot="watermark"
                                title="Imagem da marca d’água"
                                hint="Fundo transparente funciona melhor. A imagem ocupa 60% da largura do papel, centralizada."
                                url={letterhead.watermarkImageUrl}
                                width={letterhead.watermarkImageWidth}
                                height={letterhead.watermarkImageHeight}
                                onChange={applyLetterhead}
                            />
                        </div>
                    )}
                </div>

                {/* Cabeçalho e rodapé padrão */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Cabeçalho e rodapé do sistema
                    </h2>
                    <p className="text-sm text-base-content/60 mb-4">
                        O layout padrão imprime o logo, o nome, o CNPJ, o
                        endereço e o contato da filial. Um timbrado bem feito já
                        traz essa informação na arte — nesse caso, oculte-os para
                        não sair duplicado. Sem imagem correspondente enviada, o
                        padrão continua aparecendo: melhor repetido do que um
                        documento sem identificação nenhuma.
                    </p>
                    <div className="flex flex-col gap-3">
                        <label className="label cursor-pointer justify-start gap-3">
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary toggle-sm"
                                checked={hideDefaultHeader}
                                onChange={e =>
                                    setHideDefaultHeader(e.target.checked)
                                }
                            />
                            <span className="label-text">
                                Ocultar o cabeçalho padrão
                            </span>
                        </label>
                        <label className="label cursor-pointer justify-start gap-3">
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary toggle-sm"
                                checked={hideDefaultFooter}
                                onChange={e =>
                                    setHideDefaultFooter(e.target.checked)
                                }
                            />
                            <span className="label-text">
                                Ocultar o rodapé padrão
                            </span>
                        </label>
                    </div>
                </div>

                {/* Documentos vinculados */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Documentos
                    </h2>
                    <p className="text-sm text-base-content/60 mb-4">
                        Marque os documentos que devem usar este timbrado. Sem
                        nenhum marcado, ele só entra em uso se for o padrão da
                        filial.
                    </p>

                    <div className="flex flex-col gap-5">
                        {LETTERHEAD_DOCUMENT_GROUPS.map(group => (
                            <div key={group.label}>
                                <h3 className="text-sm font-semibold text-base-content/70 mb-2">
                                    {group.label}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {group.types.map(type => (
                                        <label
                                            key={type}
                                            className="label cursor-pointer justify-start gap-3 rounded-lg border border-base-300/50 px-3 py-2 hover:bg-base-200/40"
                                        >
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-secondary checkbox-sm"
                                                checked={documentTypes.includes(
                                                    type,
                                                )}
                                                onChange={() =>
                                                    toggleDocumentType(type)
                                                }
                                            />
                                            <span className="label-text text-sm">
                                                {
                                                    LETTERHEAD_DOCUMENT_TYPES[
                                                        type
                                                    ]
                                                }
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 pb-4">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/registrations/letterheads')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        Salvar
                    </button>
                </div>
            </form>
        </div>
    )
}
